import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar,
  Clock,
  Video,
  Star,
  ChevronRight,
  User,
  Settings,
  LogOut,
  Download,
  FileText,
  Play,
  Key,
  Users,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  MessageSquare,
  DollarSign,
  Phone,
  Headphones,
  Bell,
  CalendarPlus,
  Check,
  X,
  Loader2,
  RefreshCw,
  AlertTriangle
} from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { AvailabilityCalendar } from "@/components/AvailabilityCalendar";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState(null);
  const [expandedUpcoming, setExpandedUpcoming] = useState(null);
  const [downloadingRecording, setDownloadingRecording] = useState(null);
  const [downloadingTranscript, setDownloadingTranscript] = useState(null);
  const [slotRequests, setSlotRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [respondingTo, setRespondingTo] = useState(null);
  const [paymentModal, setPaymentModal] = useState(null); // {request, slot, expert_name, amount}
  const [processingPayment, setProcessingPayment] = useState(false);
  const [retryingPayment, setRetryingPayment] = useState(null); // booking_id being retried
  
  // Cancel and Reschedule states
  const [cancelModal, setCancelModal] = useState(null); // booking to cancel
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [rescheduleModal, setRescheduleModal] = useState(null); // booking to reschedule - shows calendar
  // Awaiting-review list — paid + past bookings without a review (session_review_v1).
  // Renders a card above Past Sessions when non-empty; renders nothing otherwise.
  const [awaitingReview, setAwaitingReview] = useState([]);
  const [selectedRescheduleSlot, setSelectedRescheduleSlot] = useState(null); // selected slot from calendar
  const [rescheduling, setRescheduling] = useState(false);
  const [acceptRescheduleModal, setAcceptRescheduleModal] = useState(null); // for accepting proposed slots

  useEffect(() => {
    fetchBookings();
    fetchSlotRequests();
    fetchAwaitingReview();
    // Component-local fetchers + we want fire-once-on-mount; the rule
    // doesn't know that and would have us wrap in useCallback for the
    // same effect — see CLAUDE.md mention in the exhaustive-deps sweep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Awaiting-review fetch — best-effort. Failures are silenced because
  // an empty list and a failed fetch render identically (no card), and
  // there's no recovery a user could take.
  const fetchAwaitingReview = async () => {
    try {
      const res = await axios.get(`${API}/bookings/me/awaiting-review`);
      setAwaitingReview(res.data?.bookings || []);
    } catch {
      setAwaitingReview([]);
    }
  };

  const fetchSlotRequests = async () => {
    setLoadingRequests(true);
    try {
      const response = await axios.get(`${API}/user/slot-requests`);
      setSlotRequests(response.data.requests || []);
    } catch (error) {
      console.error("Error fetching slot requests:", error);
    } finally {
      setLoadingRequests(false);
    }
  };

  // Show payment confirmation modal before accepting
  const handleSlotSelect = (request, slot) => {
    // Calculate amount based on expert's hourly rate and duration
    const hourlyRate = request.expert_hourly_rate || 500;
    const duration = request.duration_minutes || 60;
    const amount = (hourlyRate * duration) / 60;
    
    setPaymentModal({
      request,
      slot,
      expert_name: request.expert_name,
      duration,
      amount,
      currency: request.expert_currency || 'USD'
    });
  };

  // Process payment after user confirms
  const handleConfirmAndPay = async () => {
    if (!paymentModal) return;
    
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/slot-requests/${paymentModal.request.id}/accept-and-pay`, {
        selected_slot: paymentModal.slot,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.success("Booking confirmed!");
        setPaymentModal(null);
        fetchSlotRequests();
        fetchBookings();
      }
    } catch (error) {
      console.error("Error processing payment:", error);
      toast.error("Failed to process payment. Please try again.");
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleRetryPayment = async (bookingId) => {
    setRetryingPayment(bookingId);
    try {
      const response = await axios.post(`${API}/bookings/${bookingId}/retry-payment`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem("xpert_token")}` }
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate payment. Please try again.");
    } finally {
      setRetryingPayment(null);
    }
  };

  const handleRetrySlotPayment = async (request) => {
    setRetryingPayment(request.id);
    try {
      const response = await axios.post(`${API}/slot-requests/${request.id}/accept-and-pay`, {
        selected_slot: request.accepted_slot,
        origin_url: window.location.origin
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate payment. Please try again.");
    } finally {
      setRetryingPayment(null);
    }
  };


  const handleAcceptSlot = async (requestId, slot) => {
    setRespondingTo(requestId);
    try {
      await axios.post(`${API}/slot-requests/${requestId}/respond`, {
        action: "accept",
        selected_slot: slot
      });
      toast.success("Slot accepted! The expert has been notified.");
      fetchSlotRequests();
    } catch (error) {
      console.error("Error accepting slot:", error);
      toast.error("Failed to accept slot");
    } finally {
      setRespondingTo(null);
    }
  };

  const handleRequestMore = async (requestId) => {
    setRespondingTo(requestId);
    try {
      await axios.post(`${API}/slot-requests/${requestId}/respond`, {
        action: "request_more",
        message: "I need different time options please"
      });
      toast.success("Request sent! The expert will propose new slots.");
      fetchSlotRequests();
    } catch (error) {
      console.error("Error requesting more slots:", error);
      toast.error("Failed to send request");
    } finally {
      setRespondingTo(null);
    }
  };

  const getRequestStatusBadge = (status) => {
    const badges = {
      pending: { bg: "bg-yellow-500/20", text: "text-yellow-500", label: "Pending" },
      slots_proposed: { bg: "bg-blue-500/20", text: "text-blue-500", label: "Slots Available" },
      accepted: { bg: "bg-brand-green/15", text: "text-brand-green", label: "Accepted" },
      payment_pending: { bg: "bg-amber-100", text: "text-amber-600", label: "Payment Pending" },
      more_requested: { bg: "bg-amber-100", text: "text-amber-600", label: "Awaiting Response" }
    };
    return badges[status] || badges.pending;
  };

  // Cancel booking handler
  const handleCancelBooking = async () => {
    if (!cancelModal) return;
    
    setCancelling(true);
    try {
      const response = await axios.post(`${API}/bookings/${cancelModal.id}/cancel`, {
        reason: cancelReason
      });
      
      toast.success(`Booking cancelled. ${response.data.refund_percentage}% refund ($${response.data.refund_amount?.toFixed(2) || 0}) will be processed.`);
      setCancelModal(null);
      setCancelReason("");
      fetchBookings();
    } catch (error) {
      console.error("Error cancelling booking:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel booking");
    } finally {
      setCancelling(false);
    }
  };

  // Handle slot selection from calendar for rescheduling
  const handleRescheduleSlotSelect = (slot) => {
    setSelectedRescheduleSlot(slot);
  };

  // Confirm reschedule with selected slot from calendar
  const handleConfirmReschedule = async () => {
    if (!rescheduleModal || !selectedRescheduleSlot) return;
    
    setRescheduling(true);
    try {
      await axios.post(`${API}/bookings/${rescheduleModal.id}/reschedule/select-from-calendar`, {
        selected_slot: {
          date: selectedRescheduleSlot.date,
          time: selectedRescheduleSlot.time,
          time_12h: selectedRescheduleSlot.time_12h
        }
      });
      toast.success("Session rescheduled successfully!");
      setRescheduleModal(null);
      setSelectedRescheduleSlot(null);
      fetchBookings();
    } catch (error) {
      console.error("Error rescheduling:", error);
      toast.error(error.response?.data?.detail || "Failed to reschedule");
    } finally {
      setRescheduling(false);
    }
  };

  // Accept reschedule slot handler (for expert-proposed slots)
  const handleAcceptRescheduleSlot = async (booking, slot) => {
    try {
      await axios.post(`${API}/bookings/${booking.id}/reschedule/accept`, {
        selected_slot: slot
      });
      toast.success("Session rescheduled successfully!");
      setAcceptRescheduleModal(null);
      fetchBookings();
    } catch (error) {
      console.error("Error accepting reschedule slot:", error);
      toast.error(error.response?.data?.detail || "Failed to reschedule");
    }
  };

  // Calculate refund info for display
  const getRefundInfo = (booking) => {
    const scheduledDatetime = new Date(`${booking.scheduled_date}T${booking.scheduled_time}`);
    const now = new Date();
    const hoursUntil = (scheduledDatetime - now) / (1000 * 60 * 60);
    
    if (hoursUntil < 24) {
      return { percentage: 50, message: "Less than 24 hours before session" };
    }
    return { percentage: 75, message: "More than 24 hours before session" };
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings/me`);
      setBookings(response.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-brand-green/15 text-brand-green";
      case "pending":
        return "bg-yellow-500/20 text-yellow-500";
      case "cancelled":
        return "bg-red-500/20 text-red-500";
      case "completed":
        return "bg-blue-500/20 text-blue-500";
      default:
        return "bg-slate-500/20 text-slateline-400";
    }
  };

  const handleDownloadRecording = async (bookingId) => {
    setDownloadingRecording(bookingId);
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}/recording`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("xpert_token")}` }
      });
      const { download_url, filename } = response.data;
      // Open Daily.co download URL directly (avoids streaming large file through backend)
      const link = document.createElement('a');
      link.href = download_url;
      link.setAttribute('download', filename || `session_${bookingId}_recording.mp4`);
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Recording download started");
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(error.response?.data?.detail || "Recording not available for this session");
      } else {
        toast.error("Failed to download recording");
      }
    } finally {
      setDownloadingRecording(null);
    }
  };

  const handleDownloadTranscript = async (bookingId) => {
    setDownloadingTranscript(bookingId);
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}/transcript`);
      // Create a text file from the transcript
      const blob = new Blob([response.data.transcript], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `session_${bookingId}_transcript.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Transcript downloaded successfully");
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Transcript not available for this session");
      } else {
        toast.error("Failed to download transcript");
      }
    } finally {
      setDownloadingTranscript(null);
    }
  };

  const upcomingBookings = bookings.filter(b => 
    (b.status === "confirmed" || b.status === "pending") && 
    new Date(b.scheduled_date) >= new Date()
  );
  const pastBookings = bookings.filter(b => 
    b.payment_status === "paid" && 
    (b.status === "completed" || new Date(b.scheduled_date) < new Date())
  );

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  return (
    <div className="min-h-screen bg-canvas">
      <MarketingNav />
      
      <section className="pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div 
                className="bg-white border border-slateline-200 p-6 rounded-sm sticky top-28"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slateline-200">
                  <div className="w-12 h-12 bg-brand-greenPale/60 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-brand-green" />
                  </div>
                  <div>
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-sm text-slateline-400">{user?.email}</p>
                  </div>
                </div>

                <nav className="space-y-2">
                  <button className="w-full flex items-center gap-3 px-4 py-3 bg-brand-greenPale/40 text-brand-green rounded-sm">
                    <Calendar className="w-5 h-5" />
                    My Bookings
                  </button>
                  <button
                    onClick={() => navigate("/api-keys")}
                    className="w-full flex items-center gap-3 px-4 py-3 text-slateline-500 hover:text-brand-green hover:bg-brand-greenPale/40 rounded-sm transition-colors"
                  >
                    <Key className="w-5 h-5" />
                    Claude MCP Keys
                  </button>
                  <button
                    onClick={() => navigate("/become-expert")}
                    className="w-full flex items-center gap-3 px-4 py-3 text-slateline-500 hover:text-ink hover:bg-slateline-100 rounded-sm transition-colors"
                  >
                    <Star className="w-5 h-5" />
                    Become Expert
                  </button>
                  <a 
                    href="tel:+971558945236"
                    className="w-full flex items-center gap-3 px-4 py-3 text-slateline-500 hover:text-brand-green hover:bg-brand-greenPale/40 rounded-sm transition-colors"
                    data-testid="sidebar-support-btn"
                  >
                    <Headphones className="w-5 h-5" />
                    Support
                  </a>
                  <button 
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-slateline-500 hover:text-ink hover:bg-slateline-100 rounded-sm transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                    Sign Out
                  </button>
                </nav>
                
                {/* Support Card */}
                <div className="mt-6 pt-6 border-t border-slateline-200">
                  <div className="bg-brand-greenPale/40 border border-brand-green/20 rounded-sm p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Phone className="w-4 h-4 text-brand-green" />
                      <span className="text-sm font-medium text-brand-green">Need Help?</span>
                    </div>
                    <a 
                      href="tel:+971558945236" 
                      className="text-ink hover:text-brand-greenHover transition-colors font-medium"
                    >
                      +971 55 894 5236
                    </a>
                    <p className="text-xs text-slateline-400 mt-1">Available 24/7</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3 space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h1 className="font-display text-4xl mb-2">My Bookings</h1>
                <p className="text-slateline-500">Manage your upcoming and past sessions</p>
              </motion.div>

              {/* Stats */}
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-3 gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <div className="bg-white border border-slateline-200 p-6 rounded-sm">
                  <p className="text-slateline-500 text-sm mb-2">Upcoming Sessions</p>
                  <p className="font-display text-3xl text-brand-green">{upcomingBookings.length}</p>
                </div>
                <div className="bg-white border border-slateline-200 p-6 rounded-sm">
                  <p className="text-slateline-500 text-sm mb-2">Completed Sessions</p>
                  <p className="font-display text-3xl">{pastBookings.length}</p>
                </div>
                <div className="bg-white border border-slateline-200 p-6 rounded-sm">
                  <p className="text-slateline-500 text-sm mb-2">Total Invested</p>
                  <p className="font-display text-3xl">${bookings.filter(b => b.payment_status === 'paid').reduce((sum, b) => sum + b.amount, 0).toFixed(0)}</p>
                </div>
              </motion.div>

              {/* Upcoming Bookings */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h2 className="font-display text-2xl mb-4">Upcoming Sessions</h2>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(2)].map((_, i) => (
                      <div key={i} className="bg-white border border-slateline-200 p-6 rounded-sm h-32 animate-pulse" />
                    ))}
                  </div>
                ) : upcomingBookings.length > 0 ? (
                  <div className="space-y-4">
                    {upcomingBookings.map((booking) => (
                      <div 
                        key={booking.id}
                        className="bg-white border border-slateline-200 rounded-sm overflow-hidden"
                      >
                        {/* Session Header - Clickable */}
                        <div 
                          className="p-6 cursor-pointer hover:bg-white/80 transition-colors"
                          onClick={() => setExpandedUpcoming(expandedUpcoming === booking.id ? null : booking.id)}
                        >
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                              <div className="w-14 h-14 bg-slateline-100 rounded-full flex items-center justify-center">
                                {booking.expert_anonymous ? (
                                  <User className="w-7 h-7 text-slateline-400" />
                                ) : (
                                  <span className="font-display text-lg text-brand-green">
                                    {booking.expert_name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'EX'}
                                  </span>
                                )}
                              </div>
                              <div>
                                <h3 className="font-semibold text-lg">
                                  {booking.expert_anonymous ? "Anonymous Expert" : booking.expert_name}
                                </h3>
                                <p className="text-sm text-slateline-400">{booking.expert_title || "Expert Consultation"}</p>
                                <div className="flex items-center gap-4 text-sm text-slateline-500 mt-1">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    {new Date(booking.scheduled_date).toLocaleDateString('en-US', {
                                      weekday: 'short',
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {booking.scheduled_time}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                                {booking.status}
                              </span>
                              {expandedUpcoming === booking.id ? (
                                <ChevronUp className="w-5 h-5 text-slateline-500" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-slateline-500" />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {expandedUpcoming === booking.id && (
                          <div className="border-t border-slateline-200 p-6 bg-canvas/50">
                            {/* Session Details Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                              <div className="flex items-center gap-3">
                                <Calendar className="w-5 h-5 text-brand-green" />
                                <div>
                                  <p className="text-xs text-slateline-400">Date & Time</p>
                                  <p className="font-medium">
                                    {new Date(booking.scheduled_date).toLocaleDateString('en-US', {
                                      weekday: 'long',
                                      month: 'long',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </p>
                                  <p className="text-sm text-slateline-500">{booking.scheduled_time}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <Clock className="w-5 h-5 text-brand-green" />
                                <div>
                                  <p className="text-xs text-slateline-400">Duration</p>
                                  <p className="font-medium">{formatDuration(booking.duration_minutes)}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <DollarSign className="w-5 h-5 text-brand-green" />
                                <div>
                                  <p className="text-xs text-slateline-400">Amount Paid</p>
                                  <p className="font-medium">${booking.amount}</p>
                                </div>
                              </div>
                            </div>

                            {/* Topic/Notes */}
                            {booking.notes && (
                              <div className="mb-6 p-4 bg-white rounded-sm border border-slateline-300">
                                <p className="text-xs text-slateline-400 mb-1 flex items-center gap-2">
                                  <MessageSquare className="w-4 h-4" />
                                  Session Topic
                                </p>
                                <p className="text-slateline-700">{booking.notes}</p>
                              </div>
                            )}

                            {/* Expert Notes (if shared) */}
                            {booking.expert_notes && (
                              <div className="mb-6 p-4 bg-white rounded-sm border border-slateline-300">
                                <p className="text-xs text-slateline-400 mb-1 flex items-center gap-2">
                                  <FileText className="w-4 h-4" />
                                  Notes from Expert
                                </p>
                                <p className="text-slateline-700">{booking.expert_notes}</p>
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex flex-wrap gap-3">
                              {/* Complete Payment — shown when payment is still pending */}
                              {booking.payment_status === 'pending' && booking.status !== 'cancelled' && (
                                <Button
                                  onClick={() => handleRetryPayment(booking.id)}
                                  disabled={retryingPayment === booking.id}
                                  className="bg-brand-green hover:bg-brand-greenHover text-white font-semibold"
                                  data-testid={`complete-payment-${booking.id}`}
                                >
                                  {retryingPayment === booking.id ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <DollarSign className="w-4 h-4 mr-2" />
                                  )}
                                  Complete Payment
                                </Button>
                              )}
                              {booking.meeting_link ? (
                                <Button
                                  onClick={() => navigate(`/meeting/${booking.id}`)}
                                  className="bg-brand-green hover:bg-brand-greenHover text-ink"
                                  data-testid={`join-meeting-${booking.id}`}
                                >
                                  <Video className="w-4 h-4 mr-2" />
                                  Join Video Call
                                </Button>
                              ) : booking.status === 'confirmed' && booking.payment_status === 'paid' ? (
                                <Button
                                  onClick={() => navigate(`/meeting/${booking.id}`)}
                                  className="bg-brand-green hover:bg-brand-greenHover text-ink"
                                  data-testid={`join-meeting-${booking.id}`}
                                >
                                  <Video className="w-4 h-4 mr-2" />
                                  Join Video Call
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  className="border-slateline-300"
                                  disabled
                                >
                                  <Video className="w-4 h-4 mr-2" />
                                  Meeting Link Pending
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                className="border-brand-green/40 text-brand-green hover:bg-brand-greenPale/40"
                                onClick={() => navigate(`/experts/${booking.expert_id}`)}
                              >
                                View Expert Profile
                              </Button>
                              
                              {/* Reschedule Button - Opens calendar to select new slot */}
                              {booking.status !== 'cancelled' && !booking.reschedule_status && (
                                <Button
                                  variant="outline"
                                  className="border-brand-green/50 text-brand-green hover:bg-brand-green/10"
                                  onClick={() => {
                                    setRescheduleModal(booking);
                                    setSelectedRescheduleSlot(null);
                                  }}
                                  data-testid={`reschedule-${booking.id}`}
                                >
                                  <RefreshCw className="w-4 h-4 mr-2" />
                                  Reschedule
                                </Button>
                              )}
                              
                              {/* Show reschedule status - expert is proposing slots */}
                              {booking.reschedule_status === 'pending' && booking.reschedule_requested_by === 'expert' && (
                                <Button
                                  className="bg-brand-green hover:bg-brand-greenHover text-white"
                                  onClick={() => {
                                    setRescheduleModal(booking);
                                    setSelectedRescheduleSlot(null);
                                  }}
                                  data-testid={`select-new-slot-${booking.id}`}
                                >
                                  <Calendar className="w-4 h-4 mr-2" />
                                  Select New Slot
                                </Button>
                              )}
                              
                              {/* Show proposed reschedule slots from expert */}
                              {booking.reschedule_status === 'slots_proposed' && (
                                <Button
                                  className="bg-brand-green hover:bg-brand-greenHover text-white"
                                  onClick={() => setAcceptRescheduleModal(booking)}
                                  data-testid={`view-reschedule-slots-${booking.id}`}
                                >
                                  View Proposed Slots
                                </Button>
                              )}
                              
                              {/* Cancel Button */}
                              {booking.status !== 'cancelled' && (
                                <Button
                                  variant="outline"
                                  className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                                  onClick={() => setCancelModal(booking)}
                                  data-testid={`cancel-${booking.id}`}
                                >
                                  <X className="w-4 h-4 mr-2" />
                                  Cancel
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white border border-slateline-200 p-12 rounded-sm text-center">
                    <Calendar className="w-12 h-12 text-slateline-500 mx-auto mb-4" />
                    <p className="text-slateline-500 mb-4">No upcoming sessions</p>
                    <Button 
                      onClick={() => navigate("/experts")}
                      className="bg-brand-green hover:bg-brand-greenHover text-black"
                    >
                      Browse Experts
                    </Button>
                  </div>
                )}
              </motion.div>

              {/* Awaiting-review prompt — only renders when the user has
                  paid + past bookings without reviews. Sits above Past
                  Sessions so it's the first thing they see in the post-
                  session region. Closes the marketplace flywheel
                  (session_review_v1). */}
              {awaitingReview.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-8 bg-brand-greenPale border border-brand-green/30 rounded-xl p-5 sm:p-6"
                >
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">
                    Awaiting your review
                  </p>
                  <h2 className="mt-2 font-display text-xl text-ink">
                    {awaitingReview.length === 1
                      ? "Tell us how your session went."
                      : `${awaitingReview.length} sessions need your review.`}
                  </h2>
                  <p className="mt-2 text-sm text-slateline-600">
                    Your feedback helps other founders find the right expert.
                  </p>
                  <div className="mt-4 space-y-2">
                    {awaitingReview.slice(0, 5).map((b) => (
                      <div
                        key={b.booking_id}
                        className="flex items-center justify-between gap-3 bg-white border border-slateline-200 rounded-md px-4 py-3"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-ink truncate">
                            {b.expert_name || "Expert session"}
                          </p>
                          <p className="text-xs text-slateline-500 truncate">
                            {b.scheduled_at}
                            {b.duration_min ? ` · ${b.duration_min} min` : ""}
                          </p>
                        </div>
                        <Link
                          to={`/session/${b.booking_id}/review`}
                          className="inline-flex items-center h-9 px-4 rounded-md bg-brand-green hover:bg-brand-greenHover text-white text-sm font-semibold whitespace-nowrap"
                        >
                          Leave review →
                        </Link>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Past Sessions - Enhanced */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="font-display text-2xl mb-4">Past Sessions</h2>
                {pastBookings.length > 0 ? (
                  <div className="space-y-4">
                    {pastBookings.map((booking) => (
                      <div 
                        key={booking.id}
                        className="bg-white border border-slateline-200 rounded-sm overflow-hidden"
                      >
                        {/* Session Header */}
                        <div 
                          className="p-6 cursor-pointer hover:bg-white/80 transition-colors"
                          onClick={() => setExpandedSession(expandedSession === booking.id ? null : booking.id)}
                        >
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                              <div className="w-14 h-14 bg-slateline-100 rounded-full flex items-center justify-center">
                                {booking.expert_anonymous ? (
                                  <User className="w-7 h-7 text-slateline-400" />
                                ) : (
                                  <span className="font-display text-lg text-brand-green">
                                    {booking.expert_name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'EX'}
                                  </span>
                                )}
                              </div>
                              <div>
                                <h3 className="font-semibold text-lg">
                                  {booking.expert_anonymous ? "Anonymous Expert" : booking.expert_name}
                                </h3>
                                <p className="text-sm text-slateline-400">{booking.expert_title || "Expert Consultation"}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-sm text-slateline-500">
                                  {new Date(booking.scheduled_date).toLocaleDateString('en-US', { 
                                    weekday: 'short', 
                                    month: 'short', 
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </p>
                                <p className="text-xs text-slateline-400">{booking.scheduled_time}</p>
                              </div>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor('completed')}`}>
                                Completed
                              </span>
                              {expandedSession === booking.id ? (
                                <ChevronUp className="w-5 h-5 text-slateline-500" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-slateline-500" />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {expandedSession === booking.id && (
                          <div className="border-t border-slateline-200 p-6 bg-canvas/50">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                              <div className="flex items-center gap-3">
                                <Clock className="w-5 h-5 text-brand-green" />
                                <div>
                                  <p className="text-xs text-slateline-400">Duration</p>
                                  <p className="font-medium">{formatDuration(booking.duration_minutes)}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <Users className="w-5 h-5 text-brand-green" />
                                <div>
                                  <p className="text-xs text-slateline-400">Attendees</p>
                                  <p className="font-medium">
                                    You, {booking.expert_anonymous ? "Anonymous Expert" : booking.expert_name}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <Star className="w-5 h-5 text-brand-green" />
                                <div>
                                  <p className="text-xs text-slateline-400">Amount Paid</p>
                                  <p className="font-medium">${booking.amount}</p>
                                </div>
                              </div>
                            </div>

                            {booking.notes && (
                              <div className="mb-6 p-4 bg-white rounded-sm">
                                <p className="text-xs text-slateline-400 mb-1">Session Notes</p>
                                <p className="text-sm text-slateline-700">{booking.notes}</p>
                              </div>
                            )}

                            {/* Download Buttons */}
                            <div className="flex flex-wrap gap-3">
                              <Button
                                variant="outline"
                                className="border-slateline-300 hover:bg-slateline-100"
                                onClick={() => handleDownloadRecording(booking.id)}
                                disabled={downloadingRecording === booking.id}
                                data-testid={`download-recording-${booking.id}`}
                              >
                                {downloadingRecording === booking.id ? (
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                ) : (
                                  <Play className="w-4 h-4 mr-2" />
                                )}
                                Download Recording
                              </Button>
                              <Button
                                variant="outline"
                                className="border-slateline-300 hover:bg-slateline-100"
                                onClick={() => handleDownloadTranscript(booking.id)}
                                disabled={downloadingTranscript === booking.id}
                                data-testid={`download-transcript-${booking.id}`}
                              >
                                {downloadingTranscript === booking.id ? (
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                ) : (
                                  <FileText className="w-4 h-4 mr-2" />
                                )}
                                Download Transcript
                              </Button>
                              <Button
                                variant="outline"
                                className="border-brand-green/40 text-brand-green hover:bg-brand-greenPale/40"
                                onClick={() => navigate(`/experts/${booking.expert_id}`)}
                              >
                                Book Again
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white border border-slateline-200 p-12 rounded-sm text-center">
                    <Video className="w-12 h-12 text-slateline-500 mx-auto mb-4" />
                    <p className="text-slateline-500">No past sessions yet</p>
                    <p className="text-sm text-slateline-400 mt-2">Your completed sessions will appear here</p>
                  </div>
                )}
              </motion.div>

              {/* Slot Requests Section */}
              {slotRequests.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white border border-slateline-200 rounded-sm overflow-hidden"
                >
                  <div className="p-6 border-b border-slateline-200">
                    <div className="flex items-center gap-3">
                      <Bell className="w-5 h-5 text-brand-green" />
                      <h2 className="font-display text-xl">Slot Requests</h2>
                      {slotRequests.filter(r => r.status === 'slots_proposed').length > 0 && (
                        <span className="bg-blue-500 text-ink text-xs font-bold px-2 py-1 rounded-full">
                          {slotRequests.filter(r => r.status === 'slots_proposed').length} slots available
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slateline-400 mt-1">Your time slot requests and expert responses</p>
                  </div>

                  <div className="divide-y divide-slate-800">
                    {slotRequests.map((request) => {
                      const statusBadge = getRequestStatusBadge(request.status);
                      return (
                        <div key={request.id} className="p-4">
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <div>
                              <p className="font-medium text-ink">{request.expert_name}</p>
                              <p className="text-sm text-slateline-400">{request.expert_title}</p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                              {statusBadge.label}
                            </span>
                          </div>

                          {/* Show proposed slots if available */}
                          {request.status === 'slots_proposed' && request.proposed_slots && (
                            <div className="mt-4 bg-slateline-100/50 rounded-lg p-4">
                              <p className="text-sm text-slateline-700 mb-3">Choose a time slot:</p>
                              {request.expert_message && (
                                <p className="text-sm text-slateline-500 italic mb-3">"{request.expert_message}"</p>
                              )}
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {request.proposed_slots.map((slot, i) => (
                                  <button
                                    key={i}
                                    onClick={() => handleSlotSelect(request, slot)}
                                    disabled={respondingTo === request.id}
                                    className="p-3 bg-slateline-100 hover:bg-brand-greenPale/60 hover:border-brand-green border border-slateline-300 rounded-lg transition-colors text-left"
                                    data-testid={`select-slot-${i}`}
                                  >
                                    <p className="font-medium text-ink text-sm">{slot.date}</p>
                                    <p className="text-brand-green text-sm">{slot.time_12h}</p>
                                  </button>
                                ))}
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                className="mt-3 border-slateline-300"
                                onClick={() => handleRequestMore(request.id)}
                                disabled={respondingTo === request.id}
                              >
                                {respondingTo === request.id ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : null}
                                Request Different Times
                              </Button>
                            </div>
                          )}

                          {/* Show accepted slot */}
                          {request.status === 'accepted' && request.accepted_slot && (
                            <div className="mt-4 bg-brand-green/10 border border-brand-green/30 rounded-lg p-4">
                              <div className="flex items-center gap-2 text-brand-green mb-2">
                                <Check className="w-5 h-5" />
                                <span className="font-medium">Slot Confirmed</span>
                              </div>
                              <p className="text-ink">
                                {request.accepted_slot.date} at {request.accepted_slot.time_12h}
                              </p>
                              <p className="text-sm text-slateline-500 mt-1">{request.duration_minutes} minute session</p>
                            </div>
                          )}

                          {/* Show payment_pending — retry payment */}
                          {request.status === 'payment_pending' && (
                            <div className="mt-4 bg-amber-50 border border-amber-300 rounded-lg p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="text-amber-600 font-medium text-sm">Payment Incomplete</p>
                                  {request.accepted_slot && (
                                    <p className="text-ink text-sm mt-1">
                                      {request.accepted_slot.date} at {request.accepted_slot.time_12h}
                                    </p>
                                  )}
                                  <p className="text-slateline-500 text-xs mt-1">
                                    Your slot is reserved. Complete payment to confirm.
                                  </p>
                                </div>
                                <Button
                                  onClick={() => handleRetrySlotPayment(request)}
                                  disabled={retryingPayment === request.id}
                                  className="bg-brand-green hover:bg-brand-greenHover text-white font-semibold shrink-0"
                                  data-testid={`retry-payment-${request.id}`}
                                >
                                  {retryingPayment === request.id ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <DollarSign className="w-4 h-4 mr-2" />
                                  )}
                                  Pay Now
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Show pending status */}
                          {request.status === 'pending' && (
                            <p className="text-sm text-yellow-500 mt-2">
                              Waiting for expert to propose available slots...
                            </p>
                          )}

                          {/* Show more requested status */}
                          {request.status === 'more_requested' && (
                            <p className="text-sm text-amber-600 mt-2">
                              Waiting for expert to propose more slots...
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>
      
      {/* Payment Confirmation Modal */}
      <Dialog open={!!paymentModal} onOpenChange={() => setPaymentModal(null)}>
        <DialogContent className="bg-white border-slateline-300">
          <DialogHeader>
            <DialogTitle className="text-ink">Confirm & Pay</DialogTitle>
            <DialogDescription className="text-slateline-500">
              Confirm your session details and proceed to payment
            </DialogDescription>
          </DialogHeader>
          
          {paymentModal && (
            <div className="space-y-4">
              <div className="bg-slateline-100/50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slateline-500">Expert</span>
                  <span className="text-ink font-medium">{paymentModal.expert_name}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slateline-500">Date</span>
                  <span className="text-ink">{paymentModal.slot?.date}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slateline-500">Time</span>
                  <span className="text-ink">{paymentModal.slot?.time_12h || paymentModal.slot?.time}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slateline-500">Duration</span>
                  <span className="text-ink">{paymentModal.duration} minutes</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-slateline-300">
                  <span className="text-slateline-500">Total</span>
                  <span className="text-brand-green font-bold text-lg">
                    {paymentModal.currency?.toUpperCase()} ${paymentModal.amount?.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setPaymentModal(null)}
              className="border-slateline-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAndPay}
              disabled={processingPayment}
              className="bg-brand-green hover:bg-brand-greenHover text-black"
              data-testid="confirm-pay-btn"
            >
              {processingPayment ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <DollarSign className="w-4 h-4 mr-2" />
                  Proceed to Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Cancel Booking Modal */}
      <Dialog open={!!cancelModal} onOpenChange={() => { setCancelModal(null); setCancelReason(""); }}>
        <DialogContent className="bg-white border-slateline-300">
          <DialogHeader>
            <DialogTitle className="text-ink flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Cancel Booking
            </DialogTitle>
            <DialogDescription className="text-slateline-500">
              Are you sure you want to cancel this session?
            </DialogDescription>
          </DialogHeader>
          
          {cancelModal && (
            <div className="space-y-4">
              <div className="bg-slateline-100/50 rounded-lg p-4">
                <p className="text-ink font-medium">{cancelModal.expert_name}</p>
                <p className="text-slateline-500 text-sm">
                  {cancelModal.scheduled_date} at {cancelModal.scheduled_time}
                </p>
              </div>
              
              {cancelModal.payment_status === 'paid' && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <p className="text-yellow-500 font-medium mb-1">Refund Policy</p>
                  <p className="text-slateline-700 text-sm">
                    {getRefundInfo(cancelModal).percentage}% refund ({getRefundInfo(cancelModal).message})
                  </p>
                  <p className="text-slateline-500 text-xs mt-1">
                    Estimated refund: ${(cancelModal.amount * getRefundInfo(cancelModal).percentage / 100).toFixed(2)}
                  </p>
                </div>
              )}
              
              <div>
                <label className="text-sm text-slateline-500 mb-2 block">Reason for cancellation (optional)</label>
                <Textarea
                  placeholder="Let us know why you're cancelling..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="bg-slateline-100 border-slateline-300 text-ink"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setCancelModal(null); setCancelReason(""); }}
              className="border-slateline-300"
            >
              Keep Booking
            </Button>
            <Button
              onClick={handleCancelBooking}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-500 text-ink"
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                "Cancel Booking"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Accept Reschedule Slots Modal */}
      <Dialog open={!!acceptRescheduleModal} onOpenChange={() => setAcceptRescheduleModal(null)}>
        <DialogContent className="bg-white border-slateline-300">
          <DialogHeader>
            <DialogTitle className="text-ink">Choose New Time Slot</DialogTitle>
            <DialogDescription className="text-slateline-500">
              {acceptRescheduleModal?.expert_name} has proposed new time slots for your session
            </DialogDescription>
          </DialogHeader>
          
          {acceptRescheduleModal && acceptRescheduleModal.reschedule_proposed_slots && (
            <div className="space-y-4">
              {acceptRescheduleModal.reschedule_message && (
                <p className="text-slateline-500 italic">"{acceptRescheduleModal.reschedule_message}"</p>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                {acceptRescheduleModal.reschedule_proposed_slots.map((slot, i) => (
                  <button
                    key={i}
                    onClick={() => handleAcceptRescheduleSlot(acceptRescheduleModal, slot)}
                    className="p-4 bg-slateline-100 hover:bg-brand-greenPale/60 hover:border-brand-green border border-slateline-300 rounded-lg transition-colors text-left"
                    data-testid={`accept-reschedule-slot-${i}`}
                  >
                    <p className="font-medium text-ink">{slot.date}</p>
                    <p className="text-brand-green">{slot.time_12h || slot.time}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAcceptRescheduleModal(null)}
              className="border-slateline-300"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reschedule Calendar Modal - User selects new slot from expert's calendar */}
      <Dialog 
        open={!!rescheduleModal} 
        onOpenChange={() => { 
          setRescheduleModal(null); 
          setSelectedRescheduleSlot(null); 
        }}
      >
        <DialogContent className="bg-white border-slateline-300 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-ink flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-brand-green" />
              Reschedule Session
            </DialogTitle>
            <DialogDescription className="text-slateline-500">
              Select a new time slot from {rescheduleModal?.expert_name}'s availability
            </DialogDescription>
          </DialogHeader>
          
          {rescheduleModal && (
            <div className="space-y-4">
              {/* Current booking info */}
              <div className="bg-slateline-100/50 rounded-lg p-4">
                <p className="text-sm text-slateline-500 mb-1">Current Session</p>
                <p className="text-ink font-medium">
                  {rescheduleModal.scheduled_date} at {rescheduleModal.scheduled_time}
                </p>
              </div>
              
              {/* Availability Calendar */}
              <div className="border border-slateline-300 rounded-lg overflow-hidden">
                <AvailabilityCalendar
                  expertId={rescheduleModal.expert_id}
                  expertName={rescheduleModal.expert_name}
                  hourlyRate={rescheduleModal.amount / (rescheduleModal.duration_minutes / 60)}
                  currency={rescheduleModal.currency || "USD"}
                  onSlotSelect={handleRescheduleSlotSelect}
                  selectedSlot={selectedRescheduleSlot}
                />
              </div>
              
              {/* Selected slot display */}
              {selectedRescheduleSlot && (
                <div className="bg-brand-green/10 border border-brand-green/30 rounded-lg p-4">
                  <p className="text-brand-green font-medium mb-1">New Time Selected</p>
                  <p className="text-ink">
                    {selectedRescheduleSlot.date} at {selectedRescheduleSlot.time_12h || selectedRescheduleSlot.time}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setRescheduleModal(null); setSelectedRescheduleSlot(null); }}
              className="border-slateline-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmReschedule}
              disabled={!selectedRescheduleSlot || rescheduling}
              className="bg-brand-green hover:bg-brand-greenHover text-white"
              data-testid="confirm-reschedule-btn"
            >
              {rescheduling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Rescheduling...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Confirm Reschedule
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
